import {
  Component, OnInit, ViewEncapsulation,
  forwardRef, OnDestroy, Input, NgZone,
  Output, ViewChild, ElementRef, EventEmitter, Renderer2
} from '@angular/core';

import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';

import { ScriptService } from './script.service';

import { UEditorConfig } from './ueditor.config';

const VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => UEditorComponent),
  multi: true
};
export type EVENT_TPYES = 'destroy' | 'reset' | 'focus' | 'langReady' |
  'beforeExecCommand' | 'afterExecCommand' | 'firstBeforeExecCommand' | 'beforeGetContent' |
  'afterGetContent' | 'getAllHtml' | 'beforeSetContent' | 'afterSetContent' |
  'selectionchange' | 'beforeSelectionChange' | 'afterSelectionChange';
declare var window: any;
declare var UE: any;

@Component({
  selector: 'bui-ueditor',
  template: `
    <textarea #host class="ueditor-textarea"></textarea>
    <p class="loading" *ngIf="loading">{{loadingTip}}</p>`,
  styles: [`.ueditor-textarea{display:none;}`],
  encapsulation: ViewEncapsulation.Emulated,
  providers: [VALUE_ACCESSOR]
})
export class UEditorComponent implements OnInit, OnDestroy, ControlValueAccessor {

  private instance: any;
  private value: string;
  private path: string;
  private events: any = {};

  loading = false;
  id: string;

  @Input() config: any;

  @Input() loadingTip: string = '加载中......';

  @Input() style: any = {};

  @ViewChild('host') host: ElementRef;

  @Output() onPreReady = new EventEmitter<UEditorComponent>();

  @Output() onReady = new EventEmitter<UEditorComponent>();

  @Output() onDestroy: EventEmitter<any> = new EventEmitter();

  @Output() onContentChange: EventEmitter<any> = new EventEmitter();

  onChange: Function = () => { };
  onTouched: Function = () => { };

  constructor(
    private ss: ScriptService,
    private defaultConfig: UEditorConfig,
    private zone: NgZone,
    private render: Renderer2
  ) { }

  ngOnInit() {
    this.path = this.defaultConfig && this.defaultConfig.path;
    if (!this.path) this.path = '../assets/ueditor/';
    // 构建一个虚拟id
    this.id = 'ueditor-' + new Date().getTime();
    this.host.nativeElement.id = this.id;
    // 自定义样式
    if (this.style) {
      Object.keys(this.style).forEach((key: string) => {
        this.render.setStyle(this.host.nativeElement, key, this.style[key]);
      });
    }
    // 已经存在对象无须进入懒加载模式
    if (window.UE) {
      this.init();
      return;
    }

    this.ss.load(this.path, true).getChangeEmitter().subscribe(res => {
      this.init();
    });

  }

  init(options?: any) {
    if (!window.UE) {
      throw new Error('ueditor js文件加载失败');
    }
    if (this.instance) {
      return;
    }

    // registrer hook
    if (this.defaultConfig && this.defaultConfig.hook) {
      if (!this.defaultConfig.hook_finished) {
        this.defaultConfig.hook_finished = true;
        this.defaultConfig.hook(UE);
      }
    }

    this.loading = false;
    this.onPreReady.emit(this);
    this.zone.runOutsideAngular(() => {
      const opt = Object.assign({
        UEDITOR_HOME_URL: this.path
      }, this.defaultConfig && this.defaultConfig.options, this.config, options);

      let ueditor = UE.getEditor(this.id, opt);
      ueditor.ready(() => {
        this.zone.run(() => {
          this.instance = ueditor;
          this.value && this.instance.setContent(this.value);
          this.onReady.emit(this);
        });
      })

      ueditor.addListener('contentChange', () => {
        this.updateValue(ueditor.getContent());
      });
    });
  }

  private updateValue(value: string): void {
    this.zone.run(() => {
      this.value = value;

      this.onChange(this.value);
      this.onTouched(this.value);

      this.onContentChange.emit(this.value);
    });
  }

  private destroy(): void {
    if (this.instance) {
      for (let k of this.events) {
        this.instance.removeListener(k, this.events[k]);
      }
      this.instance.removeListener('ready');
      this.instance.removeListener('contentChange');
      this.instance.destroy();
      this.instance = null;
    }
    this.onDestroy.emit();
  }

  /**
   * 获取UE实例
   *
   */
  public get Instance(): any {
    return this.instance;
  }

  /**
   * 设置编辑语言
   */
  setLang(lang: 'zh-cn' | 'en'): void {
    this.ss.loadScript(`${this.path}/lang/${lang}/${lang}.js`).then(res => {
      this.destroy();

      //清空语言
      if (!UE._bak_I18N) {
        UE._bak_I18N = UE.I18N;
      }
      UE.I18N = {};
      UE.I18N[lang] = UE._bak_I18N[lang];

      this.init();
    });
  }

  /**
    * 添加编辑器事件
    */
  addListener(eventName: EVENT_TPYES, fn: Function): void {
    if (this.events[eventName]) return;
    this.events[eventName] = fn;
    this.instance.addListener(eventName, fn);
  }

  /**
   * 移除编辑器事件
   */
  removeListener(eventName: EVENT_TPYES): void {
    if (!this.events[eventName]) return;
    this.instance.removeListener(eventName, this.events[eventName]);
    delete this.events[eventName];
  }

  writeValue(value: any): void {
    this.value = value;
    if (this.instance) {
      this.instance.setContent(value);
    }
  }

  public registerOnChange(fn: (_: any) => {}): void { this.onChange = fn; }
  public registerOnTouched(fn: () => {}): void { this.onTouched = fn; }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.instance.setDisabled();
    } else {
      this.instance.setEnabled();
    }
  }
  ngOnDestroy(): void {
    this.destroy();
  }
}
